<template>
  <div class="widget-section">
    <div class="widget-grid">

      <!-- Left column: Settings -->
      <div class="card">
        <div class="card-header">
          <i class="fa fa-gear card-header--icon"></i>
          <span class="card-header--title">Widget Settings</span>
        </div>
        <div class="card-content">
          <form class="card-content--form">
            <div class="form-group">
              <label>Affiliation Code</label>
              <select v-model="selectedAffiliationCode">
                <option v-for="(code, index) in masterCodeArray" :value="code" :key="'embed-code-' + index">
                  {{ code }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Origin Port</label>
              <select v-model="selectedOriginPort">
                <option value="">&mdash; None &mdash;</option>
                <option v-for="port in availablePorts" :value="port.portCode" :key="'origin-' + port.portCode">
                  {{ port.portName }} ({{ port.portCode }})
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Destination Port</label>
              <select v-model="selectedDestinationPort">
                <option value="">&mdash; None &mdash;</option>
                <option v-for="port in availablePorts" :value="port.portCode" :key="'dest-' + port.portCode">
                  {{ port.portName }} ({{ port.portCode }})
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Design</label>
              <select v-model="selectedDesign">
                <option value="v1">Classic (v1)</option>
                <option value="v2">Modern (v2)</option>
              </select>
            </div>

            <div class="form-group">
              <label class="checkbox">
                <input type="checkbox" v-model="openInNewWindow" class="checkbox-input" />
                <span class="checkbox-label">Open redirections in new window</span>
              </label>
            </div>
          </form>

          <div class="info-box" v-if="!hasWidgetApiKey">
            <i class="fa fa-info-circle"></i>
            <span>Need an API key? <a href="mailto:affiliates@ferryhopper.com">Contact us</a></span>
          </div>
        </div>
      </div>

      <!-- Right column: Code snippets and Preview -->
      <div>
        <div class="card">
          <div class="card-header card-header--right">
            <span class="card-header--title">Installation Script</span>
          </div>
          <div class="card-content">
            <p class="code-description">Add this script at the bottom of your website's <code>&lt;body&gt;</code> tag:</p>
            <div class="code-block">
              <pre><code>{{ installationScript }}</code></pre>
              <button class="copy-btn copy-btn--code" @click="copyToClipboardText(installationScript)">
                <i class="fa fa-clipboard"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header card-header--right">
            <span class="card-header--title">Widget Element</span>
          </div>
          <div class="card-content">
            <p class="code-description">Place this element anywhere on your page:</p>
            <div class="code-block">
              <pre><code>{{ widgetElementExample }}</code></pre>
              <button class="copy-btn copy-btn--code" @click="copyToClipboardText(widgetElementExample)">
                <i class="fa fa-clipboard"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header card-header--right">
            <span class="card-header--title">Live Preview</span>
          </div>
          <div class="card-content">
            <div class="widget-preview-container">
              <ferryhopper-widget :key="widgetKey" :origin="selectedOriginPort" :destination="selectedDestinationPort" :design="selectedDesign"></ferryhopper-widget>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import PortsRepository from '@/repositories/ports'
import { mapGetters } from 'vuex';

export default {
  name: 'EmbeddableWidgetPage',
  data() {
    return {
      selectedAffiliationCode: '',
      openInNewWindow: false,
      widgetApiKeyPlaceholder: 'YOUR_WIDGET_API_KEY',
      liveWidgetApiKey: 'ivrn11sWEJ81xoJaVLFd48Pfo3LI7GFR5x9vBgz6',
      selectedOriginPort: 'PIR',
      selectedDestinationPort: 'HER',
      selectedDesign: 'v1',
    }
  },
  created() {
    if (this.getAppUser && this.getAppUser.affiliationCodes.length > 0) {
      this.selectedAffiliationCode = this.getAppUser.affiliationCodes[0];
    }
  },
  mounted() {
    this.loadWidgetScript();
  },
  watch: {
    getAppUser(oldValue, newValue) {
      if (newValue.affiliationCodes.length > 0) {
        this.selectedAffiliationCode = this.getAppUser.affiliationCodes[0];
      }
    }
  },
  computed: {
    ...mapGetters({
      getAppUser: 'authentication/getAppUser'
    }),
    masterCodeArray() {
      return this.getAppUser?.affiliationCodes || [];
    },
    availablePorts() {
      return PortsRepository.getPorts();
    },
    hasWidgetApiKey() {
      return !!this.getAppUser?.widgetApiKeyValue;
    },
    widgetApiKeyForCopy() {
      return this.hasWidgetApiKey ? this.getAppUser.widgetApiKeyValue : this.widgetApiKeyPlaceholder;
    },
    installationScript() {

      const affiliateNameLine = `\n  fhiw('affiliateName', '${this.getAppUser?.code}');`;

      const affiliateLine = this.selectedAffiliationCode
        ? `\n  fhiw('affiliate', '${this.selectedAffiliationCode}');`
        : `\n  // fhiw('affiliate', 'YOUR_AFFILIATION_ID');`;
      const targetLine = this.openInNewWindow
        ? `\n  fhiw('target', '_blank');`
        : '';
      const designLine = this.selectedDesign === 'v2'
        ? `\n  fhiw('design', 'v2');`
        : '';

      return `<script>
  window.fhiw = window.fhiw || function() {
    (fhiw.init = fhiw.init || []).push(arguments)
  };
  fhiw('client', '${this.widgetApiKeyForCopy}');${affiliateNameLine}${affiliateLine}${targetLine}${designLine}
<\/script>
<script async src="https://widgets.ferryhopper.com/widget.js"><\/script>`;
    },
    widgetElementExample() {
      const attrs = [];
      if (this.selectedOriginPort) attrs.push(`origin="${this.selectedOriginPort}"`);
      if (this.selectedDestinationPort) attrs.push(`destination="${this.selectedDestinationPort}"`);
      if (this.selectedDesign === 'v2') attrs.push(`design="v2"`);
      const attrStr = attrs.length ? ' ' + attrs.join(' ') : '';
      return `<ferryhopper-widget${attrStr}></ferryhopper-widget>`;
    },
    widgetKey() {
      return `${this.selectedOriginPort}-${this.selectedDestinationPort}-${this.selectedDesign}`;
    },
  },
  methods: {
    copyToClipboardText(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('Copy');
        document.body.removeChild(textarea);
      }
    },
    loadWidgetScript() {
      // Initialize the fhiw function
      window.fhiw = window.fhiw || function() {
        (window.fhiw.init = window.fhiw.init || []).push(arguments);
      };
      window.fhiw('client', this.liveWidgetApiKey);
      window.fhiw('design', this.selectedDesign);

      if (this.selectedAffiliationCode) {
        window.fhiw('affiliate', this.selectedAffiliationCode);
      }

      // Load the widget script if not already loaded
      if (!document.querySelector('script[src="https://widgets.ferryhopper.com/widget.js"]')) {
        const script = document.createElement('script');
        script.src = 'https://widgets.ferryhopper.com/widget.js';
        script.async = true;
        document.body.appendChild(script);
      }
    },
  },
}
</script>

<style scoped>
.widget-section {
  padding: 2rem 0;
}

.widget-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
}

.code-description {
  color: #666;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.code-description code {
  background-color: #f4f4f4;
  padding: 0.15rem 0.4rem;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
}

.code-block {
  position: relative;
  background-color: #2d2d2d;
  border-radius: 6px;
  overflow: hidden;
}

.code-block pre {
  margin: 0;
  padding: 1rem;
  overflow-x: auto;
}

.code-block code {
  background: none;
  color: #f8f8f2;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  white-space: pre;
}

.copy-btn--code {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.copy-btn--code:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.info-box {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #e8f4fd;
  border-radius: 6px;
  margin-top: 1.5rem;
  color: #1a73e8;
  font-size: 0.9rem;
}

.info-box i {
  margin-top: 0.1rem;
}

.info-box a {
  color: #1a73e8;
  text-decoration: underline;
}

.widget-preview-container {
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 6px;
  min-height: 200px;
}

@media (max-width: 900px) {
  .widget-grid {
    grid-template-columns: 1fr;
  }
}
</style>
